import { Link, Navigate } from "react-router-dom"
import { Loader2 } from "lucide-react"
import type { AppShell } from "@/App"
import { AREAS, areaByKey } from "@/lib/areas"
import { cn } from "@/lib/utils"

/**
 * Menu inicial ao estilo Odoo: um mosaico por área a que o utilizador tem
 * acesso. Com uma só área vai direto para ela — um vendedor não ganha um
 * clique a mais.
 */
export function Launcher({ shell }: { shell: AppShell | null }) {
  const areas = shell?.areas
  if (!shell || !areas) {
    return (
      <div className="flex justify-center py-24 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    )
  }

  const tiles = AREAS.filter((a) => a.enabled && areas.includes(a.key))
  if (tiles.length === 1) {
    return <Navigate to={tiles[0]!.path} replace />
  }
  if (tiles.length === 0) {
    // Não devia acontecer (o servidor dá sempre o simulador), mas não deixamos
    // o utilizador num ecrã vazio.
    return <Navigate to={areaByKey("simulador")!.path} replace />
  }

  return (
    <div className="mx-auto max-w-3xl py-10">
      <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 md:grid-cols-4">
        {tiles.map((a) => {
          const Icon = a.icon
          return (
            <Link
              key={a.key}
              to={a.path}
              className="group flex flex-col items-center gap-3 rounded-xl p-2 text-center outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <span
                className={cn(
                  "flex h-20 w-20 items-center justify-center rounded-2xl text-white shadow-sm transition-transform group-hover:-translate-y-0.5 group-hover:shadow-md",
                  a.tone,
                )}
              >
                <Icon className="h-10 w-10" strokeWidth={1.75} />
              </span>
              <span>
                <span className="block text-sm font-medium">{a.label}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {a.description}
                </span>
              </span>
            </Link>
          )
        })}
      </div>
    </div>
  )
}
